/**
 * Shared admin API helpers. Every admin page reuses these to keep the
 * fetch/error/401 flow identical across the Command Center.
 */
import { clearAdminToken, readAdminToken } from "@/components/admin/AdminGuard";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

async function request(method, path, body) {
  const token = readAdminToken();
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 || res.status === 403) {
    clearAdminToken();
    // Bounce back to the unlock screen — session expired / invalidated.
    window.location.assign("/admin");
    throw new Error("Unauthorized");
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Raw HTML error pages (Cloudflare 504) are never surfaced verbatim.
    const looksHtml = /^\s*</.test(text) || /<html|<!doctype/i.test(text);
    let parsed = null;
    if (!looksHtml) {
      try { parsed = JSON.parse(text); } catch (_) { /* not JSON */ }
    }
    const detail = parsed && typeof parsed === "object" ? parsed.detail : null;
    const msg = (detail && typeof detail === "object" && detail.message) ||
                (typeof detail === "string" ? detail : null) ||
                (looksHtml ? "Serverfout — de aanvraag duurde te lang. Probeer het opnieuw." : text) ||
                res.statusText;
    const err = new Error(`${res.status} ${msg}`);
    err.status = res.status;
    // `body` carries the structured FastAPI response (`{detail: {...}}`)
    // so callers can render machine-readable reasons (e.g. publish
    // readiness failures).
    err.body = parsed;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

export const adminApi = {
  summary: () => request("GET", "/api/admin/stats/summary"),
  activity: (limit = 20) => request("GET", `/api/admin/activity?limit=${limit}`),
  listArticles: () => request("GET", "/api/admin/journal/articles"),
  inbox: (limit = 10) => request("GET", `/api/admin/journal/inbox?limit=${limit}`),
  publishArticle: (id) => request("POST", `/api/admin/journal/articles/${id}/publish`),
  unpublishArticle: (id) => request("POST", `/api/admin/journal/articles/${id}/unpublish`),
  deleteArticle: (id) => request("DELETE", `/api/admin/journal/articles/${id}`),
  translateArticle: (id) => request("POST", `/api/admin/journal/articles/${id}/translate?save=true`),
  retranslateArticleNL: (id) => request("POST", `/api/admin/journal/articles/${id}/retranslate-nl`),
  listMessages: () => request("GET", "/api/admin/share-thoughts"),
  listWaitlist: () => request("GET", "/api/admin/waitlist"),
  // Daily Inspiration Library
  diList: (f = {}) => request("GET", `/api/admin/daily-inspiration?search=${encodeURIComponent(f.search || "")}&author=${encodeURIComponent(f.author || "")}&work=${encodeURIComponent(f.work || "")}&theme=${encodeURIComponent(f.theme || "")}&status=${encodeURIComponent(f.status || "")}&active=${encodeURIComponent(f.active || "")}&page=${f.page || 1}&limit=${f.limit || 50}`),
  diMeta: () => request("GET", "/api/admin/daily-inspiration/meta"),
  diPreview: (date) => request("GET", `/api/admin/daily-inspiration/preview?date=${date}`),
  diIntegrity: () => request("GET", "/api/admin/daily-inspiration/integrity"),
  diPatch: (id, patch) => request("PATCH", `/api/admin/daily-inspiration/${id}`, patch),
  diVerify: (id, notes) => request("POST", `/api/admin/daily-inspiration/${id}/verify`, { notes }),
  // Students / onboarding
  listStudents: (search = "") =>
    request("GET", `/api/admin/students${search ? "?search=" + encodeURIComponent(search) : ""}`),
  resetStudentOnboarding: (id) => request("POST", `/api/admin/students/${id}/reset-onboarding`),
  setMembershipStatus: (id, status) => request("POST", `/api/admin/students/${id}/membership-status`, { status }),
  // Founders Wall of Honor
  listFounders: () => request("GET", "/api/admin/founders"),
  confirmFounder: (email) => request("POST", "/api/admin/founders/confirm", { email }),
  updateFounder: (id, patch) => request("PATCH", `/api/admin/founders/${id}`, patch),
  // Lexicon
  listLexicon: ({ category, search, includeDisabled = true } = {}) => {
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (search) q.set("search", search);
    if (!includeDisabled) q.set("include_disabled", "false");
    const qs = q.toString();
    return request("GET", `/api/admin/lexicon${qs ? "?" + qs : ""}`);
  },
  createLexicon: (payload) => request("POST", "/api/admin/lexicon", payload),
  updateLexicon: (id, payload) => request("PATCH", `/api/admin/lexicon/${id}`, payload),
  deleteLexicon: (id) => request("DELETE", `/api/admin/lexicon/${id}`),
  importLexicon: (terms) => request("POST", "/api/admin/lexicon/import", { terms }),
  exportLexicon: () => request("GET", "/api/admin/lexicon/export"),
  testTranslation: (text, target_language = "nl") =>
    request("POST", "/api/admin/lexicon/test-translation", { text, target_language }),
  // AutoSEO incident controls
  setAutoseoPaused: (paused) => request("POST", "/api/admin/journal/autoseo/pause", { paused }),
  journalDedupe: (dryRun) => request("POST", "/api/admin/journal/dedupe", { dryRun }),
  autoseoRollback: (since) => request("POST", "/api/admin/journal/autoseo/rollback", { since }),
  autoseoLog: () => request("GET", "/api/admin/journal/autoseo/log?limit=20"),
  runCleanup: (id) => request("POST", `/api/admin/journal/articles/${id}/cleanup`),
  cleanupAudit: () => request("GET", "/api/admin/journal/autoseo-cleanup-audit"),
  autoseoImportStatus: () => request("GET", "/api/admin/journal/autoseo/import-status"),
  globalSearch: (q) => request("GET", `/api/admin/search?q=${encodeURIComponent(q)}`),
  jobsOverview: () => request("GET", "/api/admin/jobs/overview"),
  translateAsync: (articleId) => request("POST", `/api/admin/journal/articles/${articleId}/translate-async`),
  newsletterList: () => request("GET", "/api/admin/newsletter"),
  newsletterCreate: (body) => request("POST", "/api/admin/newsletter", body),
  newsletterUpdate: (id, body) => request("PATCH", `/api/admin/newsletter/${id}`, body),
  newsletterDelete: (id) => request("DELETE", `/api/admin/newsletter/${id}`),
  newsletterSend: (id, dryRun) => request("POST", `/api/admin/newsletter/${id}/send`, { dry_run: !!dryRun }),
  seoOverview: () => request("GET", "/api/admin/seo/overview"),
  seoScanStart: () => request("POST", "/api/admin/seo/link-scan"),
  seoScanLatest: () => request("GET", "/api/admin/seo/link-scan/latest"),
  journalCalendar: (month) => request("GET", `/api/admin/journal/calendar?month=${encodeURIComponent(month)}`),
  planArticle: (id, date) => request("PATCH", `/api/admin/journal/articles/${id}/plan`, { planned_publish_date: date }),
  pubStudioFormats: (articleId, lang = "") => request("GET", `/api/admin/publishing-studio/formats/${articleId}${lang ? `?lang=${lang}` : ""}`),
  editorialStats: () => request("GET", "/api/admin/journal/editorial/stats"),
  editorialArticles: (params) => request("GET", `/api/admin/journal/editorial/articles?${new URLSearchParams(params)}`),
  editorialExport: async (params) => {
    const token = readAdminToken();
    const res = await fetch(`${API}/api/admin/journal/editorial/export?${new URLSearchParams(params)}`, {
      headers: { Authorization: token ? `Bearer ${token}` : "" },
    });
    if (!res.ok) throw new Error(`${res.status} export failed`);
    return res.blob();
  },
  refsBackfillDryRun: () => request("GET", "/api/admin/journal/references/backfill"),
  numberingMode: () => request("GET", "/api/admin/journal/numbering-mode"),
  setNumberingMode: (mode) => request("POST", "/api/admin/journal/numbering-mode", { mode }),
  refsBackfillExecute: () => request("POST", "/api/admin/journal/references/backfill"),
  approveArticleLang: (id, lang) => request("POST", `/api/admin/journal/articles/${id}/approve`, { lang, approved: true }),
  // Stoic Logic media slots
  listLogicMedia: () => request("GET", "/api/admin/academy/logic/media"),  setLogicMedia: (assetKey, payload) =>
    request("POST", `/api/admin/academy/logic/media/${encodeURIComponent(assetKey)}`, payload),
  // Stoic Logic CMS
  getLogicFull: () => request("GET", "/api/admin/academy/logic/full"),
  putLogicLesson: (phase, lesson, payload) =>
    request("PUT", `/api/admin/academy/logic/lesson/${encodeURIComponent(phase)}/${encodeURIComponent(lesson)}`, payload),
  putLogicPhase: (slug, payload) => request("PUT", `/api/admin/academy/logic/phase/${encodeURIComponent(slug)}`, payload),
  putLogicCourse: (payload) => request("PUT", "/api/admin/academy/logic/course", payload),
  putLogicTerm: (id, payload) => request("PUT", `/api/admin/academy/logic/term/${encodeURIComponent(id)}`, payload),
  putLogicChamber: (payload) => request("PUT", "/api/admin/academy/logic/chamber", payload),
  getLogicRevisions: (key) =>
    request("GET", `/api/admin/academy/logic/revisions${key ? `?entityKey=${encodeURIComponent(key)}` : ""}`),
  // Stoic Physics media slots + CMS
  listPhysicsMedia: () => request("GET", "/api/admin/academy/physics/media"),
  setPhysicsMedia: (assetKey, payload) =>
    request("POST", `/api/admin/academy/physics/media/${encodeURIComponent(assetKey)}`, payload),
  getPhysicsFull: () => request("GET", "/api/admin/academy/physics/full"),
  putPhysicsLesson: (phase, lesson, payload) =>
    request("PUT", `/api/admin/academy/physics/lesson/${encodeURIComponent(phase)}/${encodeURIComponent(lesson)}`, payload),
  putPhysicsPhase: (slug, payload) => request("PUT", `/api/admin/academy/physics/phase/${encodeURIComponent(slug)}`, payload),
  putPhysicsCourse: (payload) => request("PUT", "/api/admin/academy/physics/course", payload),
  putPhysicsTerm: (id, payload) => request("PUT", `/api/admin/academy/physics/term/${encodeURIComponent(id)}`, payload),
  putPhysicsChamber: (payload) => request("PUT", "/api/admin/academy/physics/chamber", payload),
  getPhysicsRevisions: (key) =>
    request("GET", `/api/admin/academy/physics/revisions${key ? `?entityKey=${encodeURIComponent(key)}` : ""}`),
  // Unified Academy media manager (Logic + Physics + Ethics)
  academyMediaOverview: () => request("GET", "/api/admin/academy/media-overview"),
  patchAcademyMedia: (discipline, assetKey, payload) =>
    request("PATCH", `/api/admin/academy/media/${encodeURIComponent(discipline)}/${encodeURIComponent(assetKey)}`, payload),
  clearAcademyMedia: (discipline, assetKey) =>
    request("DELETE", `/api/admin/academy/media/${encodeURIComponent(discipline)}/${encodeURIComponent(assetKey)}`),
  // Site Media Registry (Phase 3b-2)
  listSiteMedia: () => request("GET", "/api/admin/site-media"),
  setSiteMediaSlot: (slot, content_id) =>
    request("PUT", `/api/admin/site-media/${encodeURIComponent(slot)}`, { content_id }),
  clearSiteMediaSlot: (slot) =>
    request("DELETE", `/api/admin/site-media/${encodeURIComponent(slot)}`),
  // Content Vault
  listVault: ({ search, status, cursor, limit = 60 } = {}) => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (status) q.set("status", status);
    if (cursor) q.set("cursor", cursor);
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    return request("GET", `/api/admin/vault/assets${qs ? "?" + qs : ""}`);
  },
  getVaultAsset: (id) => request("GET", `/api/admin/vault/assets/${id}`),
  patchVaultAsset: (id, payload) => request("PATCH", `/api/admin/vault/assets/${id}`, payload),
  deleteVaultAsset: (id) => request("DELETE", `/api/admin/vault/assets/${id}`),
  analyzeVaultAsset: (id) => request("POST", `/api/admin/vault/assets/${id}/analyze`),
  applyVaultAnalysis: (id, proposal, options = {}) =>
    request("POST", `/api/admin/vault/assets/${id}/apply-analysis`, {
      proposal,
      promote_title: !!options.promoteTitle,
      model: options.model || "",
    }),
  vaultAssetIntegrity: (id) => request("GET", `/api/admin/vault/assets/${id}/integrity`),
  vaultPublishReadiness: (id) => request("GET", `/api/admin/vault/assets/${id}/publish-readiness`),
  vaultUnprocessedCount: () => request("GET", `/api/admin/vault/jobs/unprocessed-count`),
  // Publishing Studio
  pubStudioListArticles: () =>
    request("GET", "/api/admin/publishing-studio/articles"),
  pubStudioHistory: (articleId) =>
    request("GET", `/api/admin/publishing-studio/history/${articleId}`),
  pubStudioLinkedInPreview: (articleId) =>
    request("GET", `/api/admin/journal/articles/${articleId}/linkedin-preview`),
  pubStudioGenerate: (articleId, platform) =>
    request("POST", "/api/admin/publishing-studio/generate", {
      article_id: articleId,
      platform,
    }),
  pubStudioNewsletter: (articleIds) =>
    request("POST", "/api/admin/publishing-studio/newsletter/generate", {
      article_ids: articleIds,
    }),
  pubStudioAnalytics: () =>
    request("GET", "/api/admin/publishing-studio/analytics"),
  startVaultBatchAnalyze: (payload = {}) =>
    request("POST", `/api/admin/vault/jobs/analyze-batch`, {
      scope: payload.scope || "unprocessed",
      promote_title: payload.promoteTitle !== false,
    }),
  getVaultJob: (jobId) => request("GET", `/api/admin/vault/jobs/${jobId}`),
  cancelVaultJob: (jobId) => request("POST", `/api/admin/vault/jobs/${jobId}/cancel`),
  // Phase 3b-1 — Resolver / Usage / Replace
  resolveAsset: (contentId, adminMode = true) =>
    request("GET", adminMode ? `/api/admin/vault/resolve/${contentId}` : `/api/vault/resolve/${contentId}`),
  vaultUsage: (id) => request("GET", `/api/admin/vault/assets/${id}/usage`),
  replaceVaultBytes: async (id, file) => {
    const token = readAdminToken();
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API}/api/admin/vault/assets/${id}/replace`, {
      method: "POST",
      headers: { Authorization: token ? `Bearer ${token}` : "" },
      body: fd,
    });
    if (res.status === 401 || res.status === 403) { clearAdminToken(); window.location.assign("/admin"); throw new Error("Unauthorized"); }
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
    return res.json();
  },
  // Upload does NOT go through `request()` because it needs multipart.
  vaultFileUrl: (id, variant = "thumb") => {
    const token = readAdminToken();
    return `${API}/api/admin/vault/file/${id}?variant=${variant}&auth=${encodeURIComponent(token || "")}`;
  },
  uploadVault: async (file, title = "") => {
    const token = readAdminToken();
    const fd = new FormData();
    fd.append("file", file);
    if (title) fd.append("title", title);
    const res = await fetch(`${API}/api/admin/vault/upload`, {
      method: "POST",
      headers: { Authorization: token ? `Bearer ${token}` : "" },
      body: fd,
    });
    if (res.status === 401 || res.status === 403) {
      clearAdminToken();
      window.location.assign("/admin");
      throw new Error("Unauthorized");
    }
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      let detail = text;
      try { const j = JSON.parse(text); if (j && j.detail) detail = j.detail; } catch { /* noop */ }
      throw new Error(detail || `HTTP ${res.status}`);
    }
    return res.json();
  },
};

export const API_BASE = API;
export { readAdminToken };
